import { BaseSerializer } from '@core/serializers/baseSerializer';

class OrderSerializer extends BaseSerializer {
  // ============================================
  // SERIALIZE: UI -> API
  // ============================================

  serialize(data) {
    console.log('🚀 ~ OrderSerializer ~ serialize ~ data:', data);
    if (!data) return {};

    // Get cart from store for pricing data
    const cartStore = window.$stores?.cart;
    const cart = cartStore?.state?.cart;
    console.log('🚀 ~ OrderSerializer ~ serialize ~ cart:', cart);

    // Handle checkout initiation with payment data
    const items = data.cartData?.items || data.items || [];

    const serialized = {
      userId: data.userId,
      cartId: data.cartData?.id || data.cartId,
      restaurantId: data.restaurant.id,
      restaurantName: data.restaurant.storeName,
      deliveryOption: data.deliveryOption,
      deliveryLocation: data.deliveryLocation,

      // Distance & duration (from fees calculation step)
      distanceKm: data.distanceKm || 0,
      durationMin:
        (data.durationMin || 0) + (data.estimatedCookingTimeMin || 0),

      // Flatten items for order history
      items: items.map((item) => ({
        dishId: item.dishId || item.id,
        title: item.title || item.name,
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || item.price || 0,
        totalPrice:
          item.totalPrice ||
          (item.unitPrice || item.price || 0) * (item.quantity || 1),
        image: item.principalImageUrl || item.image || item.dishImage || '',
        options: item.options || [],
        drinks: item.drinks || [],
        specialInstructions: item.specialInstructions || ''
      })),

      // Summary (customer name + first product info)
      summary: data.summary || {
        customerName: data.userData
          ? `${data.userData?.firstName} ${data.userData?.lastName}`
          : '',
        productName: items[0]?.title || items[0]?.name || '',
        productImage: items[0]?.principalImageUrl || items[0]?.image || ''
      },

      paymentMethod: data.paymentData?.method || data.paymentMethod || 'cash',
      payment: {
        status: 'pending',
        method: data.paymentData?.method || data.paymentMethod || 'cash',
        ...(data.paymentData?.operator
          ? { operator: data.paymentData.operator }
          : {}),
        ...(data.paymentData?.provider
          ? { provider: data.paymentData.provider }
          : {}),
        ...(data.paymentData?.phoneNumber
          ? { phoneNumber: data.paymentData.phoneNumber }
          : {}),
        ...(data.paymentData?.walletId
          ? { walletId: data.paymentData.walletId }
          : {})
      },
      customerNote: data.customerNote,
      drinks: data.cartData?.drinks || cart?.drinks || [],
      metadata: {
        customerNote: data.customerNote,
        estimatedCookingTimeMin: data.estimatedCookingTimeMin || 0,
        deliveryDurationMin: data.durationMin || 0,
        durationMin:
          (data.durationMin || 0) + (data.estimatedCookingTimeMin || 0),
        drinks: data.cartData?.drinks || cart?.drinks || [],
        restaurantLocation: data.restaurant?.location || {
          address: data.restaurant?.address,
          locationDescription: data.restaurant?.locationDescription,
          lng: data.restaurant?.lng,
          lat: data.restaurant?.lat,
          city: data.restaurant?.city,
          country: data.restaurant?.country
        },
        walletRestaurant: `wallet_${data.restaurant.id}`
      }
    };

    return serialized;
  }

  // ============================================
  // DESERIALIZE: API -> UI
  // ============================================

  /**
   * Deserialize orders list response from API
   */
  deserializeList(raw) {
    const apiData = raw?.data;
    console.log('🚀 ~ OrderSerializer ~ deserializeList ~ apiData:', apiData);
    if (!apiData) {
      return [];
    }

    // Handle response format: new API: { data: { data: [...], total, limit, offset } }
    // Legacy API: { data: { orders: [...] } }
    const orders =
      apiData?.data?.data ||
      apiData?.data?.orders ||
      apiData?.orders ||
      apiData?.data ||
      [];
    console.log('🚀 ~ OrderSerializer ~ deserializeList ~ orders:', orders);

    return orders.map((order) => this.deserializeOrder(order));
  }

  /**
   * Deserialize single order from API
   */
  deserializeOrder(apiData) {
    if (!apiData) return null;

    // Extract pricing from nested pricing object or flat fields
    const pricing = apiData.pricing || {};
    const payment = apiData.payment || {};
    const metadata = apiData.metadata || {};
    const timelineArr = apiData.timeline || [];

    const getTimestamp = (statusValue) => {
      const entry = timelineArr.find((t) => t.status === statusValue);
      return entry ? entry.timestamp : null;
    };

    const getTimelineMeta = (statusValue) => {
      const entry = timelineArr.find((t) => t.status === statusValue);
      return entry && entry.metadata ? entry.metadata : {};
    };

    const deliveryMeta = getTimelineMeta('picked_up');
    const deliveryRider = deliveryMeta.rider || apiData.delivery?.rider || {};
    const estimatedArrival =
      deliveryMeta.estimatedArrival ||
      apiData.delivery?.estimatedArrival ||
      null;

    const restaurantLocation = metadata.restaurantLocation || {};
    const pickupAddressStr =
      restaurantLocation.address || apiData.pickupAddress || '';

    const deliveryAddress = apiData.deliveryAddress || {};
    const deliveryLocation = apiData.deliveryLocation || deliveryAddress;

    // Handle items/products array
    const itemsArray = apiData.items || apiData.products || [];
    const globalDrinks = metadata.drinks || apiData.drinks || [];

    const deliveryCode = apiData.deliveryCode || apiData.deliveryReference||"";
    const deliveryCodeFinal = deliveryCode.includes('-')
      ? deliveryCode.split('-')[1]
      : deliveryCode;

    return {
      id: apiData.id || apiData._id,
      orderNumber: apiData.orderNumber || apiData.reference || apiData.id,
      reference: apiData.reference,
      status: apiData.status,
      userId: apiData.userId,
      customerNote: apiData.customerNote || '',
      distanceKm: pricing.distance || apiData.distanceKm || 0,
      durationMin: metadata.durationMin || apiData.durationMin || 0,
      deliveryDurationMin:
        metadata.deliveryDurationMin || apiData.durationMin || 0,
      estimatedCookingTimeMin: metadata.estimatedCookingTimeMin || 0,

      riderId: apiData.riderId || deliveryRider.riderId,
      riderName: apiData.rider?.name || apiData.riderName || deliveryRider.name,
      riderPhone:
        apiData.rider?.phone || apiData.phoneNumber || deliveryRider.phone,
      riderLat:
        apiData.rider?.lat ||
        apiData.riderLat ||
        apiData.riderLocation?.latitude,
      riderLng:
        apiData.rider?.lng ||
        apiData.riderLng ||
        apiData.riderLocation?.longitude,

      // Restaurant/Store info
      restaurantId: apiData.restaurantId,
      restaurantName: apiData.restaurantName,
      restaurant: {
        id: apiData.restaurantId || apiData.storeId || apiData.restaurant?.id,
        name:
          apiData.restaurantName ||
          apiData.storeName ||
          apiData.restaurant?.name ||
          'Restaurant',
        image: apiData.restaurant?.image || apiData.restaurant?.coverImage,
        address: pickupAddressStr
      },

      // Pickup location (from API format)
      pickupLabel: apiData.restaurantName || 'Restaurant',
      pickupAddress: pickupAddressStr,
      pickupCity: restaurantLocation.city,
      pickupLat:
        restaurantLocation.lat ||
        restaurantLocation.coordinates?.latitude ||
        restaurantLocation.coordinates?.lat,
      pickupLng:
        restaurantLocation.lng ||
        restaurantLocation.coordinates?.longitude ||
        restaurantLocation.coordinates?.lng,

      // Destination location
      destinationLabel:
        deliveryLocation.city ||
        deliveryLocation.address ||
        deliveryLocation.street,
      destinationAddress: deliveryLocation.address || deliveryLocation.street,
      destinationCity: deliveryLocation.city,
      destinationLat:
        deliveryLocation.lat ||
        deliveryLocation.coordinates?.latitude ||
        deliveryLocation.coordinates?.lat,
      destinationLng:
        deliveryLocation.lng ||
        deliveryLocation.coordinates?.longitude ||
        deliveryLocation.coordinates?.lng,
      destinationContactName: deliveryLocation.contactName,
      destinationContactPhone: deliveryLocation.contactPhone,

      // Delivery info (enriched from API)
      deliveryId: apiData.delivery?.id || apiData.deliveryId,
      deliveryCode: deliveryCode,
      deliveryReference: apiData.deliveryReference || apiData.deliveryCode,
      deliveryStatus: apiData.delivery?.status || apiData.deliveryStatus,

      // Delivery section from API delivery object
      delivery: {
        id: apiData.delivery?.id || apiData.deliveryId,
        code: deliveryCode,
        status: apiData.delivery?.status || apiData.deliveryStatus,
        estimatedArrival: estimatedArrival,
        address: deliveryLocation,
        rider: deliveryRider.name
          ? {
              name: deliveryRider.name,
              phone: deliveryRider.phone,
              vehicle: deliveryRider.vehicle
            }
          : null
      },

      rider:
        deliveryRider.name || apiData.rider?.name || apiData.riderName
          ? {
              id: deliveryRider.riderId || apiData.riderId,
              name:
                deliveryRider.name || apiData.rider?.name || apiData.riderName,
              phone:
                deliveryRider.phone ||
                apiData.rider?.phone ||
                apiData.phoneNumber,
              vehicle:
                deliveryRider.vehicle || apiData.rider?.vehicle || 'Moto',
              rating: deliveryRider.rating || apiData.rider?.rating || '5.0',
              profilePicture:
                deliveryRider.profilePicture || apiData.rider?.profilePicture
            }
          : null,

      // Products/Items
      products: itemsArray.map((item) => ({
        id: item.id || item.menuItemId || item.dishId,
        title: item.title || item.name || item.dishName,
        principalImageUrl:
          item.principalImageUrl ||
          item.image ||
          item.dishImage ||
          apiData.summary?.productImage ||
          '',
        storeId: item.storeId || item.restaurantId,
        storeName: item.storeName || apiData.restaurantName || 'Restaurant',
        numberOrdered: item.quantity || item.numberOrdered || 1,
        price: item.price || item.unitPrice || 0,
        totalPrice:
          item.totalPrice ||
          (item.price || item.unitPrice || 0) * (item.quantity || 1),
        discountedPrice: item.discountedPrice,
        options: item.options || [],
        drinks: item.drinks || []
      })),

      // Global Drinks
      drinks: globalDrinks,

      // Fees and totals (from API pricing object)
      totalPrice: pricing.total || apiData.total || apiData.totalPrice || 0,
      subtotal: pricing.subtotal || apiData.subtotal || 0,
      deliveryFee: pricing.deliveryFee || apiData.deliveryFee || 0,
      originalDeliveryFee:
        pricing.originalDeliveryFee ||
        apiData.originalDeliveryFee ||
        pricing.deliveryFee ||
        apiData.deliveryFee ||
        0,
      deliveryPromotion:
        pricing.deliveryPromotion || apiData.deliveryPromotion || null,
      serviceFee: pricing.serviceFee || apiData.serviceFee || 0,
      paymentFee: pricing.paymentFee || apiData.paymentFee || 0,
      discount: pricing.discount || apiData.discount || 0,

      // Payment (from API payment object)
      payment: {
        method: payment.method || apiData.paymentMethod || 'cash',
        status: payment.status || apiData.paymentStatus,
        paymentId: payment.paymentId || apiData.paymentId,
        subtotal: pricing.subtotal || apiData.subtotal || 0,
        deliveryFee: pricing.deliveryFee || apiData.deliveryFee || 0,
        originalDeliveryFee:
          pricing.originalDeliveryFee ||
          apiData.originalDeliveryFee ||
          pricing.deliveryFee ||
          apiData.deliveryFee ||
          0,
        deliveryPromotion:
          pricing.deliveryPromotion || apiData.deliveryPromotion || null,
        serviceFee: pricing.serviceFee || apiData.serviceFee || 0,
        discount: pricing.discount || apiData.discount || 0,
        total: pricing.total || apiData.total || apiData.totalPrice || 0
      },
      paymentMethod: payment.method || apiData.paymentMethod || 'cash',
      paymentStatus: payment.status || apiData.paymentStatus,
      paymentId: payment.paymentId || apiData.paymentId,

      // Timeline and tracking
      // Build stepsTimeline from times object + delivery timestamps for full lifecycle
      stepsTimeline: {
        payment:
          getTimestamp('payment_paid') ||
          getTimestamp('paid') ||
          (['paid', 'completed', 'successful', 'success'].includes(
            payment.status || apiData.paymentStatus
          )
            ? payment.paidAt || apiData.updatedAt || apiData.createdAt
            : null),
        accepted: getTimestamp('accepted'),
        preparing: getTimestamp('preparing'),
        cooked: getTimestamp('ready_for_pickup'),
        deliveryStarted: getTimestamp('picked_up'),
        delivered: getTimestamp('delivered'),
        canceled: getTimestamp('cancelled') || getTimestamp('canceled')
      },
      timeline: timelineArr,
      mergedTimeline: apiData.mergedTimeline || timelineArr,
      deliveryTimeline: apiData.deliveryTimeline || [],
      estimatedDeliveryTime: estimatedArrival || apiData.estimatedDeliveryTime,

      // Timestamps (from API times object)
      timestamps: {
        createdAt: apiData.createdAt || getTimestamp('pending'),
        confirmedAt: getTimestamp('confirmed'),
        preparingAt: getTimestamp('preparing'),
        readyAt: getTimestamp('ready_for_pickup'),
        pickedUpAt: getTimestamp('picked_up'),
        deliveredAt: getTimestamp('delivered'),
        cancelledAt: getTimestamp('cancelled') || getTimestamp('canceled')
      },
      createdAt: apiData.createdAt || getTimestamp('pending'),
      confirmedAt: getTimestamp('confirmed'),
      preparingAt: getTimestamp('preparing'),
      readyAt: getTimestamp('ready_for_pickup'),
      pickedUpAt: getTimestamp('picked_up'),
      deliveredAt: getTimestamp('delivered'),
      canceledAt: getTimestamp('cancelled') || getTimestamp('canceled'),

      // Status helpers
      canCancel: ['pending', 'confirmed'].includes(apiData.status),
      canRate:
        apiData.status === 'delivered' &&
        (!apiData.review || !apiData.review.rating),
      cancelledBy: apiData.cancelledBy || null,

      // Rating
      rating: apiData.rating,
      review: apiData.review,

      // Raw data for OrderDetails
      raw: apiData
    };
  }

  /**
   * Deserialize error response from API
   */
  deserializeError(apiError) {
    if (!apiError) {
      return {
        message: 'Une erreur est survenue',
        code: 'UNKNOWN_ERROR'
      };
    }

    return {
      message:
        apiError.message ||
        apiError.data?.message ||
        apiError.error?.message ||
        'Une erreur est survenue',
      code:
        apiError.code ||
        apiError.data?.code ||
        apiError.error?.code ||
        'API_ERROR',
      details:
        apiError.details ||
        apiError.data?.details ||
        apiError.error?.details ||
        null
    };
  }
}

// Export singleton instance
const orderSerializer = new OrderSerializer();
export default orderSerializer;
